import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { formatTimecode } from '@/core/time/format';
import type { EngineError } from '@/engine/errors';

/** Maps engine error codes to friendly, translated messages. */
export function useErrorMessage() {
  const { t } = useTranslation();
  return useCallback(
    (error: EngineError): string => {
      switch (error.code) {
        case 'unsupported-format':
          return t('load.errors.unsupportedFormat');
        case 'no-video':
          return t('load.errors.noVideo');
        case 'no-audio':
          return t('load.errors.noAudio');
        case 'codec-unsupported':
          return t('load.errors.codecUnsupported', { codec: (error.detail ?? 'unknown').toUpperCase() });
        case 'browser-unsupported':
          return t('load.errors.browserUnsupported');
        case 'no-encoder':
          return t('export.errors.noEncoder');
        case 'empty-output':
          return t('export.errors.empty');
        case 'size-limit-too-small':
          return t('export.limit.tooLong', { length: formatTimecode(Number(error.detail) || 0) });
        case 'storage':
          return t('export.errors.storage');
        case 'download-failed':
          return t('common.downloadFailed');
        case 'frames-unreadable':
          return t('privacy.errors.framesUnreadable');
        case 'source-unreadable':
          return t('load.errors.sourceUnreadable');
        case 'stalled':
          return t('export.errors.stalled');
        default:
          return error.detail ?? error.message;
      }
    },
    [t],
  );
}
